import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import Socket from '@/socket'
import constants from '@/constant'
import socketHelper from '@/helpers/socket/socket-helper'
import utils from '@/util'
import OpenAIApi from '@/api/openai'
import store from '@/vuex/store'
import i18nPresenter from '@/i18nPresenter'
import SlideApi from '@/api/slide'
import IdeasTabName from '@/constant/IdeasTabName'
import EventCategories from '@/constant/eventCategories'
import { SimpleCache } from '@/utils/cache.utils'

const cache = new SimpleCache()

// Defer to the second frame from now: the first lets Vue flush its patch, the
// second lets layout and paint settle, so whatever runs next starts on a thread
// that is actually free.
const nextTwoFrames = (fn) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(fn)
  })
}
// The action only receives `state`, so reach the shared `hasSlideEntranceFade`
// getter through the store singleton already imported here rather than widening
// every call site. That getter is the single source of truth for the surface the
// cross-fade applies to (presenting, not share/casting/add-in) and is reused by the
// templates that cancel the old directional slide-in, so the two never drift.
//
// Token guarding the `slide-vt` class: only the most recent transition may remove
// it. A superseded transition's `finished` settles while its replacement is already
// live, and an unguarded removal would un-pin that replacement mid-flight.
let activeVtToken = null

const updateSlideActiveAction = ({ commit }, payload) => {
  commit('updateSlideActive', payload)
}

const updateSlideByIdAction = ({ commit }, payload) => {
  commit('updateSlideById', payload)
}

// AHA-46422: the outgoing slide must stay on screen, untouched, until the new
// one has faded in over it. That cannot be done by holding the old slide's DOM:
// both slides share one set of nodes, plugin slides are a single cross-origin
// iframe that keep-alive re-points (a copy reloads blank), and freestyle slides
// render outside the wrapper a copy would target.
//
// A view transition is what makes it possible: the browser snapshots the frame
// the old slide is on and keeps that snapshot composited while the callback
// swaps the slide underneath. See the `::view-transition-*` rules in
// SlideWrapper.vue — the old snapshot is pinned at opacity 1 for the whole
// transition and only the new one animates, so nothing about the old slide
// moves or dims of its own accord.
//
// The snapshot is also held for as long as the swap takes (measured 66-98ms of
// mounting), so the stage is never empty — which is what the plain fade-in
// below cannot avoid.
//
// Split out of `setSlideActiveAction` so that action keeps the payload-shaping
// complexity it already had and this stays readable on its own.
const _commitSlideActiveWithEntranceFade = ({ commit, state }, payload) => {
  const previousId = (state.slideActive && state.slideActive.id) || state.lastSlideActiveId
  const shouldCrossFade = Boolean(store.getters.hasSlideEntranceFade)
    && previousId && payload.id && previousId !== payload.id
  if (!shouldCrossFade) {
    commit('setSlideActive', payload)
    // A same-slide commit landing mid-fade must not leave the stage stuck at zero
    // opacity.
    if (state.slideEnterFadePhase === 'hidden') commit('setSlideEnterFadePhase', 'in')
    return
  }
  if (typeof window.document.startViewTransition === 'function') {
    // The two paths are mutually exclusive per browser: only the fallback below sets
    // `slideEnterFadePhase` to 'hidden', and it runs only when startViewTransition is
    // absent — so on a VT-capable browser the phase is always '' here and the stage
    // is already at full opacity when the snapshot is taken.
    // Vue patches asynchronously, so the callback has to resolve only once the
    // new slide is actually in the DOM — otherwise the browser snapshots the old
    // slide twice and nothing appears to change.
    // Navigating again mid-transition is safe: Chrome abandons the in-flight
    // transition and starts the new one (verified: next then back 80ms apart
    // lands on the original slide).
    // Scope the `::view-transition-*(root)` overrides to this class so they only
    // apply to the transitions this action starts — not any future view transition
    // (e.g. an MPA `@view-transition` rule), which would otherwise silently inherit
    // our pinned old-snapshot styling. See SlideWrapper.vue. The class is dropped
    // again once the transition settles so it is not left globally live for the
    // rest of the session.
    const vtToken = {}
    activeVtToken = vtToken
    window.document.documentElement.classList.add('slide-vt')
    const transition = window.document.startViewTransition(() => {
      commit('setSlideActive', payload)
      return Vue.nextTick()
    })
    // A navigation landing mid-transition (or one fired while the tab is hidden)
    // skips this transition and rejects `ready`/`finished` with `AbortError`.
    // That is expected here, so swallow it rather than let @sentry/vue's default
    // onunhandledrejection integration report a steady stream of them. The same
    // catch also absorbs a throw from the commit surfacing as updateCallbackDone.
    // Only the most recent transition may drop the class: a superseded one must
    // not un-pin the transition that replaced it. Runs on both settle paths
    // (finished / aborted), and the terminal `.catch` also swallows the AbortError.
    const dropVtClass = () => {
      if (activeVtToken !== vtToken) return
      activeVtToken = null
      window.document.documentElement.classList.remove('slide-vt')
    }
    transition.ready.catch(() => {})
    transition.finished.then(dropVtClass).catch(dropVtClass)
    return
  }
  // Fallback where view transitions are unavailable (Safari < 18, Firefox < 144):
  // fade the new slide in on its own. The old slide still never animates — it
  // holds full opacity until the swap — but it cannot be kept on screen while the
  // new one arrives, so the stage shows only the deck's background in between.
  //
  // The hide and the swap are committed together, in one synchronous block, so
  // Vue patches them in a single render: the new slide's content is already at
  // opacity 0 the first time it is painted. Splitting them would flash the new
  // slide at full opacity for a frame before it dropped to zero to fade in.
  commit('setSlideEnterFadePhase', 'hidden')
  commit('setSlideActive', payload)
  // Wait two frames before starting the fade. Mounting the new slide blocks the
  // main thread for 50-110ms, and an animation started into that loses its
  // opening frames — that was the stutter. Waiting is free here: the stage is
  // just held at zero until the thread clears, and a static hold cannot stutter.
  nextTwoFrames(() => commit('setSlideEnterFadePhase', 'in'))
}

const setSlideActiveAction = ({ commit, state }, payload) => {
  payload = payload || {}
  payload.randomIndexId = utils.generateRandomString() // https://ahaslides.atlassian.net/browse/AHA-23989
  payload.SlideOptions = payload.SlideOptions || []

  const slideTypes = [
    'bulletPoints',
    'multipleChoice',
    'pickAnswer',
    'imageChoice',
    'scale',
  ]

  const customDefaultSlideOptionsType = ['matchPairs', 'correctOrder']

  // Create default SlideOptions
  if (slideTypes.includes(payload.type) &&
    !customDefaultSlideOptionsType.includes(payload.slideType) &&
    payload.SlideOptions.length === 0) {
    payload.SlideOptions = [
      { index: 1, order: 1, randomIndexId: utils.generateRandomString() },
      { index: 2, order: 2, randomIndexId: utils.generateRandomString() },
      { index: 3, order: 3, randomIndexId: utils.generateRandomString() },
    ]
  }

  if (payload.type === 'voiceQA' && payload.SlideOptions.length === 0) {
    payload.SlideOptions = [
      {
        index: 1, order: 1, title: 'Your name', randomIndexId: utils.generateRandomString(),
      },
      {
        index: 2, order: 2, title: 'Your sitting area', randomIndexId: utils.generateRandomString(),
      },
    ]
  }

  if (payload.type === 'ideas') {
    payload.limitChoice = Math.min(constants.MAX_VOTE_IDEAS, payload.limitChoice)
    payload.limitChoice = Math.max(constants.MIN_VOTE_IDEAS, payload.limitChoice)
  } else {
    payload.limitChoice = Math.max(2, payload.limitChoice || 2)
  }

  if (payload.type === 'bulletPoints') {
    payload.bulletPoints = payload.bulletPoints || []
  }

  if (state.route.name === 'PresentationDetail' &&
    state.lastSlideActiveId &&
    payload.id &&
    state.lastSlideActiveId !== payload.id
  ) {
    socketHelper.leaveRoomSlide(state.presentation.uniqueAccessCode, state.lastSlideActiveId)
    socketHelper.joinRoomSlide(state.presentation.uniqueAccessCode, payload.id)
  }

  _commitSlideActiveWithEntranceFade({ commit, state }, payload)
}

const checkSlideMovingDirectionAction = ({ commit }, slideActiveId) => {
  commit('checkSlideMovingDirection', slideActiveId)
}

const concatSlideOptionsAction = ({ commit, state }, payload) => {
  const currentSlideOptions = state.slideActive.SlideOptions || []
  const newSlideOptions = payload.slideOptions
  commit('updateSlideById', {
    id: payload.slideId,
    SlideOptions: currentSlideOptions.concat(newSlideOptions),
  })
}

const generateOptionsAction = async ({ commit }, {
  slideId,
  onGenerateOptionsSuccess,
  onGenerateOptionsError,
}) => {
  try {
    const { slide: slideObject } = await OpenAIApi.generateOptions({ slideId })

    if (!slideObject) {
      store.dispatch('setSyncLoadingAction', 3)
      return
    }

    slideObject.otherCorrectQuiz = slideObject.otherCorrectQuizJson || slideObject.otherCorrectQuiz

    await commit('updateSlideById', {
      id: slideId,
      ...slideObject,
    })
    onGenerateOptionsSuccess(slideObject.SlideOptions)
    utils.showToastSuccess('Options have been generated successfully!')
  } catch (error) {
    utils.showToastError(i18nPresenter.t('Something went wrong. Please try again.'))
    onGenerateOptionsError(error)
  }
}

const updateSlideCanvasBlockByIdAction = async ({ commit, state }, payload) => {
  if (!payload.canvasBlocksUrl) return null

  if (state.isBlockHistory) {
    // User is actively editing: defer the KV-sync update so we don't overwrite
    // locally-added or in-progress blocks. Store the latest payload; it will be
    // replayed by flushPendingCanvasBlockAction once the editor blurs (AHA-15513).
    commit('setPendingCanvasBlockPayload', { ...payload })
    return null
  }

  const canvasBlocks = await fetch(payload.canvasBlocksUrl)
    .then((res) => res.json())
    .then((body) => ({ body }))
    .catch((err) => {
      Sentry.captureException(err)
      return null
    })
  if (!canvasBlocks || !canvasBlocks.body) return null
  payload.canvasBlocks = canvasBlocks.body
  commit('updateSlideById', payload)
  return null
}

const flushPendingCanvasBlockAction = async ({ commit, state }) => {
  const payload = state.pendingCanvasBlockPayload
  if (!payload) return
  commit('setPendingCanvasBlockPayload', null)

  const canvasBlocks = await fetch(payload.canvasBlocksUrl)
    .then((res) => res.json())
    .then((body) => ({ body }))
    .catch((err) => {
      Sentry.captureException(err)
      return null
    })
  if (!canvasBlocks || !canvasBlocks.body) return
  payload.canvasBlocks = canvasBlocks.body
  commit('updateSlideById', payload)
}

const getSlideAttributesAction = async ({ rootGetters }, slideId) => {
  const canUseSlidePlugin = rootGetters['slidePlugin/canUseSlidePlugin']
  const attributeList = await SlideApi.getAttributesBySlideId({
    slideId,
    ...(canUseSlidePlugin ? { resignImage: true } : {}),
  })
  return (attributeList ?? []).reduce((obj, attr) => {
    obj[attr.type] = attr.attributes
    return obj
  }, {})
}

const slideAttributesCacheKey = (slideId) => `getSlideAttributesAction-${slideId}`

// Bumped from 5s so the host can prefetch the NEXT slide's attributes during
// the current slide and still get a cache hit when the presenter advances (see
// PreloadIframeHost). Two purge paths keep the longer TTL from serving stale
// data: local writes drop the key inline (upsertSlideAttributeAction below),
// and any inbound `slideAttributes/updateAttributes` mutation — i.e. an edit
// arriving over live sync (MQTT kvSync) from another client or a plugin's own
// request — drops it via purgeSlideAttributesCacheAction / the cache-purge
// store plugin.
const SLIDE_ATTRIBUTES_CACHE_MAX_AGE_SECONDS = 60

const getSlideAttributesWithCacheAction = async ({ dispatch }, slideId) => {
  return cache.invoke(() => {
    return dispatch('getSlideAttributesAction', slideId)
  }, {
    key: slideAttributesCacheKey(slideId),
    maxAgeInSeconds: SLIDE_ATTRIBUTES_CACHE_MAX_AGE_SECONDS,
  })
}

const upsertSlideAttributeAction = async (_, { slideId, attributeKey, attributeValue }) => {
  const result = await SlideApi.upsertSlideAttribute(slideId, {
    attributeKey,
    attributeValue,
  })
  // Write-through invalidation: drop the cached bootstrap so the next mount
  // re-reads fresh data instead of serving a stale value within the TTL window.
  cache.purge(slideAttributesCacheKey(slideId))
  return result
}

// Invalidate the cached bootstrap for a single slide. Dispatched by
// slideAttributesCachePurgePlugin on every `slideAttributes/updateAttributes`
// mutation, so an attribute change arriving over live sync (MQTT kvSync) — a
// cross-client edit, or a plugin that persists via its own iframe request
// instead of upsertSlideAttributeAction — drops the stale entry. Without this
// the 60s TTL could keep serving pre-change data to the next mount. No-op when
// the key isn't cached.
const purgeSlideAttributesCacheAction = (_, slideId) => {
  if (slideId == null) return
  cache.purge(slideAttributesCacheKey(slideId))
}

const getSlideAttributesByPresentationId = async (_, { presentationId, attributeKeys }) => {
  return SlideApi.getSlideAttributesByPresentationId({ presentationId, attributeKeys })
}

const toggleAcceptedAnswerViewAction = ({ state, dispatch }) => {
  dispatch('updateSlideActiveAction', {
    showAcceptedAnswerView: !state.slideActive.showAcceptedAnswerView,
  })
}

const changeIdeasVotingStepAction = async ({ state, dispatch }, { nextStep, isNext }) => {
  const currentStep = state.slideActive.votingStep
  utils.mixpanelTracking(
    EventCategories.PRESENTATION_EDITOR,
    'Brainstorm > Change voting step',
    `From step: ${currentStep} to step: ${nextStep}`,
  )
  if (isNext) {
    utils.mixpanelTracking(
      EventCategories.PRESENTATION_EDITOR,
      'Brainstorm > Change voting step > Enable all steps',
    )
  }
  dispatch('updateSlideActiveAction', { showQuestionImage: false, votingStep: nextStep })
  if (isNext && nextStep === IdeasTabName.RESULT) {
    const masonry = document.querySelector('#masonry-container')
    if (masonry && masonry.scrollTop) masonry.scrollTop = 0
  }
  dispatch('setSyncLoadingAction', 1)
  try {
    await SlideApi.patchSlideById(state.slideActive.id, {
      uniqueAccessCode: state.presentation.uniqueAccessCode,
      votingStep: nextStep,
    })
    dispatch('setSyncLoadingAction', 2)
  } catch (error) {
    dispatch('setSyncLoadingAction', 3)
    Sentry.captureException(error, `Ideas: Update votingStep to '${nextStep}' failed.`)
  }
}

const broadcastPluginSlideUpdateAction = async ({ state, getters }, payload) => {
  const slide = state.slideActive
  if (state.sharePresentation || state.isAdmin || !slide?.id
    || !getters.isPresenting || getters.isBackstage) return
  try {
    await SlideApi.updateSlideById(slide.id, {
      ...payload,
      accessCode: state.presentation.uniqueAccessCode,
      socketId: Socket.getSocketId(),
      presentationId: state.presentation.id,
      presenting: true,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const broadcastPluginQuizStatusAction = (ctx, quizStatus) => broadcastPluginSlideUpdateAction(ctx, { quizStatus })

export default {
  // use action which has suffix 'Action'
  broadcastPluginSlideUpdateAction,
  broadcastPluginQuizStatusAction,
  updateSlideActiveAction,
  updateSlideActive: updateSlideActiveAction,
  updateSlideByIdAction,
  setSlideActiveAction,
  setSlideActive: setSlideActiveAction,
  checkSlideMovingDirectionAction,
  concatSlideOptionsAction,
  generateOptionsAction,
  updateSlideCanvasBlockByIdAction,
  flushPendingCanvasBlockAction,
  getSlideAttributesAction,
  upsertSlideAttributeAction,
  getSlideAttributesWithCacheAction,
  purgeSlideAttributesCacheAction,
  getSlideAttributesByPresentationId,
  toggleAcceptedAnswerViewAction,
  changeIdeasVotingStepAction,

  // action hook for slide events
  'SLIDE/NAVIGATION': async () => { },
}
